import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Form,
    FormGroup,
    FormField,
    Button,
    Message,
    Checkbox,
    Segment,
    Header,
    Divider,
    Icon
} from 'semantic-ui-react';
import { canEditAll, canEditAssets, getCSRFToken, fetchWithAuth } from '../auth';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../css/Assets.css";
import { LoadingWrapper } from "../common";

const LIFECYCLE_CONFIG = {
    ordering: {
        icon: 'shopping cart',
        color: 'blue',
        fields: [
            { type: 'date', label: 'Asset Ordered Date', field: 'assetOrderedDate' },
            { type: 'date', label: 'Asset Received Date', field: 'assetReceivedDate' }
        ]
    },
    graphics: {
        icon: 'paint brush',
        color: 'purple',
        fields: [
            { type: 'date', label: 'Graphics Applied Date', field: 'assetGraphicsAppliedDate', disabledBy: 'graphicsNotNeeded' },
            { type: 'checkbox', label: 'Graphics Not Needed', field: 'graphicsNotNeeded' }
        ]
    },
    dot: {
        icon: 'car',
        color: 'orange',
        fields: [
            { type: 'date', label: 'DOT Completed Date', field: 'assetDotCompletedDate', disabledBy: 'dotNotNeeded' },
            { type: 'checkbox', label: 'DOT Not Needed', field: 'dotNotNeeded' },
            { type: 'date', label: 'DOT Stickers Applied Date', field: 'assetDotStickersAppliedDate', disabledBy: 'dotStickersNotNeeded' },
            { type: 'checkbox', label: 'DOT Stickers Not Needed', field: 'dotStickersNotNeeded' }
        ]
    },
    registration: {
        icon: 'id card',
        color: 'teal',
        fields: [
            { type: 'date', label: 'Asset Registered Date', field: 'assetRegisteredDate', disabledBy: 'assetRegistrationNotNeeded' },
            { type: 'checkbox', label: 'Registration Not Needed', field: 'assetRegistrationNotNeeded' },
            { type: 'date', label: 'Asset Plated Date', field: 'assetPlatedDate', disabledBy: 'plateNotNeeded' },
            { type: 'checkbox', label: 'Plate Not Needed', field: 'plateNotNeeded' },
            { type: 'checkbox', label: 'Temporary Plate', field: 'temporaryPlate' }
        ]
    },
    assignment: {
        icon: 'users',
        color: 'green',
        fields: [
            { type: 'checkbox', label: 'Asset Assigned To Team/Individual', field: 'assetAssignedToTeamIndividual' },
            { type: 'date', label: 'Asset Ready For Use Date', field: 'assetReadyForUseDate' }
        ]
    },
    tracker: {
        icon: 'map pin',
        color: 'red',
        fields: [
            { type: 'date', label: 'Asset Tracker Placed Date', field: 'assetTrackerPlacedDate', disabledBy: 'assetTrackerNotNeeded' },
            { type: 'checkbox', label: 'Tracker Not Needed', field: 'assetTrackerNotNeeded' }
        ]
    }
};

const INITIAL_STATE = {
    assetOrderedDate: null,
    assetReceivedDate: null,
    assetGraphicsAppliedDate: null,
    graphicsNotNeeded: false,
    assetDotCompletedDate: null,
    dotNotNeeded: false,
    assetDotStickersAppliedDate: null,
    dotStickersNotNeeded: false,
    assetRegisteredDate: null,
    assetRegistrationNotNeeded: false,
    assetPlatedDate: null,
    plateNotNeeded: false,
    temporaryPlate: false,
    assetAssignedToTeamIndividual: false,
    assetReadyForUseDate: null,
    assetTrackerPlacedDate: null,
    assetTrackerNotNeeded: false
};

const dateUtils = {
    parse: (dateString) => dateString ? new Date(dateString) : null,
    format: (date) => date ? date.toISOString().split('T')[0] : null,
    transformResponse: (data) => {
        const transformed = { ...data };
        Object.keys(INITIAL_STATE).forEach(field => {
            if (INITIAL_STATE[field] === null && data[field]) {
                transformed[field] = dateUtils.parse(data[field]);
            }
        });
        return transformed;
    },
    transformRequest: (data) => {
        const transformed = {};
        Object.keys(data).forEach(key => {
            if (INITIAL_STATE.hasOwnProperty(key) && INITIAL_STATE[key] === null && data[key] instanceof Date) {
                transformed[key] = dateUtils.format(data[key]);
            } else {
                transformed[key] = data[key];
            }
        });
        return transformed;
    }
};

const FIELD_LABELS = {
    assetOrderedDate: 'Asset Ordered Date',
    assetReceivedDate: 'Asset Received Date',
    assetGraphicsAppliedDate: 'Graphics Applied Date',
    graphicsNotNeeded: 'Graphics Not Needed',
    assetDotCompletedDate: 'DOT Completed Date',
    dotNotNeeded: 'DOT Not Needed',
    assetDotStickersAppliedDate: 'DOT Stickers Applied Date',
    dotStickersNotNeeded: 'DOT Stickers Not Needed',
    assetRegisteredDate: 'Asset Registered Date',
    assetRegistrationNotNeeded: 'Registration Not Needed',
    assetPlatedDate: 'Asset Plated Date',
    plateNotNeeded: 'Plate Not Needed',
    temporaryPlate: 'Temporary Plate',
    assetAssignedToTeamIndividual: 'Asset Assigned To Team/Individual',
    assetReadyForUseDate: 'Asset Ready For Use Date',
    assetTrackerPlacedDate: 'Asset Tracker Placed Date',
    assetTrackerNotNeeded: 'Tracker Not Needed'
};

const humanizeErrorMessage = (message) => {
    const lowerMsg = String(message).toLowerCase();
    if (lowerMsg.includes('required') || lowerMsg.includes('null')) {
        return 'This field is required';
    }
    if (lowerMsg.includes('invalid') && lowerMsg.includes('date')) {
        return 'Invalid date format';
    }
    if (lowerMsg.includes('past') || lowerMsg.includes('future')) {
        return 'Date must be in the valid range';
    }
    // Return original message if no specific pattern matches
    return message;
};

const errorUtils = {
    parseFieldErrors: (errorData) => {
        const fieldErrors = {};
        const generalErrors = [];

        if (errorData.errors && typeof errorData.errors === 'object') {
            Object.entries(errorData.errors).forEach(([field, messages]) => {
                const messageArray = Array.isArray(messages) ? messages : [messages];
                const humanizedMessages = messageArray.map(msg => humanizeErrorMessage(msg));
                
                if (INITIAL_STATE.hasOwnProperty(field)) {
                    fieldErrors[field] = {
                        label: FIELD_LABELS[field] || field,
                        messages: humanizedMessages
                    };
                } else {
                    generalErrors.push(`${field}: ${humanizedMessages.join(', ')}`);
                }
            });
        }

        if (errorData.message && !errorData.errors) {
            generalErrors.push(errorData.message);
        }

        return { fieldErrors, generalErrors };
    },

    format: (errorData) => {
        const { fieldErrors, generalErrors } = errorUtils.parseFieldErrors(errorData);
        return { fieldErrors, generalErrors };
    }
};

const useLifecycleApi = (assetId) => {
    const apiUrl = `${process.env.REACT_APP_ICC_TOOL_API_HOST}assets/${assetId}/lifecycle/`;

    // Returns transformed data, or null when no lifecycle record exists yet (404)
    const fetchLifecycle = useCallback(async () => {
        const response = await fetchWithAuth(apiUrl, {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        if (response.status === 404) return null;
        if (!response.ok) throw new Error('Failed to fetch lifecycle data');
        const data = await response.json();
        return dateUtils.transformResponse(data);
    }, [apiUrl]);

    // isNew=true → POST (create), isNew=false → PATCH (update)
    const saveLifecycle = useCallback(async (lifecycleData, isNew) => {
        const transformedData = dateUtils.transformRequest(lifecycleData);

        const response = await fetchWithAuth(apiUrl, {
            method: isNew ? 'POST' : 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken()
            },
            credentials: 'include',
            body: JSON.stringify(transformedData)
        });

        if (!response.ok) {
            const errorData = await response.json();
            // Throw error with JSON string so it can be parsed in catch block
            throw new Error(JSON.stringify(errorData));
        }
        return response.json();
    }, [apiUrl]);

    return useMemo(() => ({ fetchLifecycle, saveLifecycle }), [fetchLifecycle, saveLifecycle]);
};

const useLifecycleForm = (assetId) => {
    const [lifecycle, setLifecycle] = useState(INITIAL_STATE);
    const [isNew, setIsNew] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [succeeded, setSucceeded] = useState(false);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalErrors, setGeneralErrors] = useState([]);
    const api = useLifecycleApi(assetId);

    useEffect(() => {
        const loadData = async () => {
            try {
                const data = await api.fetchLifecycle();
                if (data === null) {
                    // No record yet — show empty form in create mode
                    setLifecycle(INITIAL_STATE);
                    setIsNew(true);
                } else {
                    setLifecycle(data);
                    setIsNew(false);
                }
                setError("");
                setFieldErrors({});
                setGeneralErrors([]);
            } catch (err) {
                console.error('Error fetching lifecycle data:', err);
                setError('Failed to load lifecycle data');
                setFieldErrors({});
                setGeneralErrors([]);
            } finally {
                setLoading(false);
            }
        };

        if (assetId !== 'newasset') {
            setLoading(true);
            loadData();
        } else {
            setLoading(false);
        }
    }, [assetId, api]);

    const updateField = (fieldName, value) => {
        setLifecycle(prev => ({ ...prev, [fieldName]: value }));
        setSucceeded(false);
        setError("");
        // Clear error for this field when user edits it
        if (fieldErrors[fieldName]) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated[fieldName];
                return updated;
            });
        }
    };

    const submit = async () => {
        setSaving(true);
        setError("");
        setFieldErrors({});
        setGeneralErrors([]);
        setSucceeded(false);

        try {
            await api.saveLifecycle(lifecycle, isNew);
            setSucceeded(true);
            setIsNew(false); // record now exists — subsequent saves use PATCH
            setFieldErrors({});
            setGeneralErrors([]);
            setTimeout(() => setSucceeded(false), 3000);
        } catch (err) {
            console.error('Error saving lifecycle data:', err);
            try {
                const errorData = JSON.parse(err.message || '{}');
                const parsed = errorUtils.format(errorData);
                setFieldErrors(parsed.fieldErrors);
                setGeneralErrors(parsed.generalErrors);
            } catch (parseErr) {
                console.error('Error parsing error response:', parseErr);
                setError(err.message || 'Failed to save lifecycle data');
            }
        } finally {
            setSaving(false);
        }
    };

    return {
        lifecycle,
        loading,
        saving,
        succeeded,
        error,
        fieldErrors,
        generalErrors,
        updateField,
        submit
    };
};

const DateField = ({ label, value, onChange, disabled, error }) => (
    <FormField disabled={disabled} error={!!error}>
        <label style={{ fontWeight: 600, color: error ? '#e74c3c' : '#444', textAlign: 'center', display: 'block' }}>
            <Icon name="calendar alternate outline" size="small" style={{ marginRight: '5px' }} />
            {label}
        </label>
        <DatePicker
            selected={value}
            onChange={onChange}
            dateFormat="MM/dd/yyyy"
            isClearable
            placeholderText="Select date"
            disabled={disabled}
            className={`ui input ${error ? 'error' : ''}`}
            wrapperClassName="datepicker-wrapper"
            popperPlacement="bottom-start"
            style={{ borderColor: error ? '#e74c3c' : undefined }}
        />
        {error && (
            <div style={{
                color: '#e74c3c',
                fontSize: '0.85em',
                marginTop: '5px',
                fontWeight: 500
            }}>
                <Icon name="warning" size="small" />
                {error.messages && error.messages[0] ? error.messages[0] : 'Invalid value'}
            </div>
        )}
    </FormField>
);

const CheckboxFieldComponent = ({ label, checked, onChange, disabled, error }) => (
    <FormField error={!!error} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Checkbox
            label={<label style={{ fontWeight: 500, color: error ? '#e74c3c' : '#555' }}>{label}</label>}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            toggle
        />
        {error && (
            <div style={{
                color: '#e74c3c',
                fontSize: '0.85em',
                marginTop: '8px',
                fontWeight: 500
            }}>
                <Icon name="warning" size="small" />
                {error.messages && error.messages[0] ? error.messages[0] : 'Invalid value'}
            </div>
        )}
    </FormField>
);

const LifecycleField = ({ config, lifecycle, onFieldChange, canEdit, fieldError }) => {
    const { type, label, field, disabledBy } = config;
    const isDisabled = !canEdit || (disabledBy && lifecycle[disabledBy]);

    if (type === 'date') {
        return (
            <DateField
                label={label}
                value={lifecycle[field]}
                onChange={(date) => onFieldChange(field, date)}
                disabled={isDisabled}
                error={fieldError}
            />
        );
    }

    if (type === 'checkbox') {
        return (
            <CheckboxFieldComponent
                label={label}
                checked={lifecycle[field]}
                onChange={(e, { checked }) => onFieldChange(field, checked)}
                disabled={!canEdit}
                error={fieldError}
            />
        );
    }

    return null;
};

const LifecycleSection = ({ title, icon, color, fields, lifecycle, onFieldChange, canEdit, fieldErrors }) => {
    const renderFieldPairs = () => {
        const pairs = [];
        for (let i = 0; i < fields.length; i += 2) {
            const field1 = fields[i];
            const field2 = fields[i + 1];

            pairs.push(
                <FormGroup widths="equal" key={`pair-${i}`}>
                    <LifecycleField
                        config={field1}
                        lifecycle={lifecycle}
                        onFieldChange={onFieldChange}
                        canEdit={canEdit}
                        fieldError={fieldErrors[field1.field]}
                    />
                    {field2 && field2.type === 'checkbox' ? (
                        <FormField style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <label style={{ visibility: 'hidden', height: '1em' }}>&nbsp;</label>
                            <LifecycleField
                                config={field2}
                                lifecycle={lifecycle}
                                onFieldChange={onFieldChange}
                                canEdit={canEdit}
                                fieldError={fieldErrors[field2.field]}
                            />
                        </FormField>
                    ) : field2 ? (
                        <LifecycleField
                            config={field2}
                            lifecycle={lifecycle}
                            onFieldChange={onFieldChange}
                            canEdit={canEdit}
                            fieldError={fieldErrors[field2.field]}
                        />
                    ) : (
                        <FormField />
                    )}
                </FormGroup>
            );
        }
        return pairs;
    };

    // Check if this section has any errors
    const hasErrors = fields.some(f => fieldErrors[f.field]);

    return (
        <Segment 
            raised 
            style={{ 
                marginBottom: '20px', 
                padding: '20px',
                borderLeft: hasErrors ? '4px solid #e74c3c' : 'none',
                backgroundColor: hasErrors ? '#fef5f5' : 'white'
            }}
        >
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <Header as="h3" style={{ display: 'inline-block', margin: 0 }}>
                    <Icon name={icon} color={hasErrors ? 'red' : color} />
                    <Header.Content>
                        {title}
                        <Header.Subheader style={{ marginTop: '5px', color: hasErrors ? '#e74c3c' : '#777' }}>
                            {title === 'Order & Receipt' 
                                ? 'Track when the asset was ordered and received'
                                : `Track ${title.toLowerCase()} information`
                            }
                        </Header.Subheader>
                    </Header.Content>
                </Header>
            </div>
            <Divider />
            {renderFieldPairs()}
        </Segment>
    );
};

const StatusMessage = ({ succeeded, error, fieldErrors, generalErrors }) => {
    if (succeeded) {
        return (
            <Message 
                positive 
                icon
                style={{ 
                    marginBottom: '20px',
                    borderLeft: '4px solid #21ba45'
                }}
            >
                <Icon name='check circle' />
                <Message.Content>
                    <Message.Header>Success</Message.Header>
                    <p>Lifecycle data has been saved successfully.</p>
                </Message.Content>
            </Message>
        );
    }

    const hasFieldOrGeneralErrors = Object.keys(fieldErrors).length > 0 || generalErrors.length > 0;

    if (hasFieldOrGeneralErrors || error) {
        return (
            <Message 
                negative 
                icon
                style={{ 
                    marginBottom: '20px',
                    borderLeft: '4px solid #db2828'
                }}
            >
                <Icon name='exclamation circle' />
                <Message.Content>
                    <Message.Header>
                        {hasFieldOrGeneralErrors ? 'Please fix the following issues:' : 'Error'}
                    </Message.Header>
                    {hasFieldOrGeneralErrors && (
                        <Message.List>
                            {Object.entries(fieldErrors).map(([field, errorData]) => (
                                <Message.Item key={field}>
                                    <strong>{errorData.label}:</strong> {errorData.messages && errorData.messages[0] ? errorData.messages[0] : 'Invalid value'}
                                </Message.Item>
                            ))}
                            {generalErrors.map((err, idx) => (
                                <Message.Item key={`general-${idx}`}>{err}</Message.Item>
                            ))}
                        </Message.List>
                    )}
                    {error && <p style={{ 
                        marginTop: hasFieldOrGeneralErrors ? '10px' : 0, 
                        fontStyle: 'italic',
                        marginBottom: 0
                    }}>{error}</p>}
                </Message.Content>
            </Message>
        );
    }

    return null;
};

export const AssetLifecycle = () => {
    const { id } = useParams();
    const { lifecycle, loading, saving, succeeded, error, fieldErrors, generalErrors, updateField, submit } = useLifecycleForm(id);
    const canEdit = canEditAssets();

    if (id === 'newasset') {
        return (
            <Message 
                info 
                icon
                style={{ 
                    margin: '20px',
                    borderLeft: '4px solid #2185d0'
                }}
            >
                <Icon name='info circle' />
                <Message.Content>
                    <Message.Header>Asset Lifecycle</Message.Header>
                    <p>Save the asset first to track lifecycle information.</p>
                </Message.Content>
            </Message>
        );
    }

    return (
        <LoadingWrapper loading={loading}>
            <div style={{ padding: '20px', background: '#f9fafb' }}>
                <Segment 
                    style={{ 
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        padding: '30px'
                    }}
                >
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                        <Header as="h1" style={{ display: 'inline-block', margin: 0 }}>
                            <Icon name="clock outline" color="blue" />
                            <Header.Content>
                                Asset Lifecycle Tracking
                                <Header.Subheader style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                                    Monitor and manage the complete lifecycle of your asset
                                </Header.Subheader>
                            </Header.Content>
                        </Header>
                    </div>
                    <Divider style={{ marginTop: '20px', marginBottom: '30px' }} />

                    <StatusMessage succeeded={succeeded} error={error} fieldErrors={fieldErrors} generalErrors={generalErrors} />

                    <Form onSubmit={(e) => { e.preventDefault(); submit(); }}>
                        <LifecycleSection
                            title="Order & Receipt"
                            icon={LIFECYCLE_CONFIG.ordering.icon}
                            color={LIFECYCLE_CONFIG.ordering.color}
                            fields={LIFECYCLE_CONFIG.ordering.fields}
                            lifecycle={lifecycle}
                            onFieldChange={updateField}
                            canEdit={canEdit}
                            fieldErrors={fieldErrors}
                        />

                        <LifecycleSection
                            title="Graphics"
                            icon={LIFECYCLE_CONFIG.graphics.icon}
                            color={LIFECYCLE_CONFIG.graphics.color}
                            fields={LIFECYCLE_CONFIG.graphics.fields}
                            lifecycle={lifecycle}
                            onFieldChange={updateField}
                            canEdit={canEdit}
                            fieldErrors={fieldErrors}
                        />

                        <LifecycleSection
                            title="DOT"
                            icon={LIFECYCLE_CONFIG.dot.icon}
                            color={LIFECYCLE_CONFIG.dot.color}
                            fields={LIFECYCLE_CONFIG.dot.fields}
                            lifecycle={lifecycle}
                            onFieldChange={updateField}
                            canEdit={canEdit}
                            fieldErrors={fieldErrors}
                        />

                        <LifecycleSection
                            title="Registration & Plates"
                            icon={LIFECYCLE_CONFIG.registration.icon}
                            color={LIFECYCLE_CONFIG.registration.color}
                            fields={LIFECYCLE_CONFIG.registration.fields}
                            lifecycle={lifecycle}
                            onFieldChange={updateField}
                            canEdit={canEdit}
                            fieldErrors={fieldErrors}
                        />

                        <LifecycleSection
                            title="Assignment & Readiness"
                            icon={LIFECYCLE_CONFIG.assignment.icon}
                            color={LIFECYCLE_CONFIG.assignment.color}
                            fields={LIFECYCLE_CONFIG.assignment.fields}
                            lifecycle={lifecycle}
                            onFieldChange={updateField}
                            canEdit={canEdit}
                            fieldErrors={fieldErrors}
                        />

                        <LifecycleSection
                            title="Tracker"
                            icon={LIFECYCLE_CONFIG.tracker.icon}
                            color={LIFECYCLE_CONFIG.tracker.color}
                            fields={LIFECYCLE_CONFIG.tracker.fields}
                            lifecycle={lifecycle}
                            onFieldChange={updateField}
                            canEdit={canEdit}
                            fieldErrors={fieldErrors}
                        />

                        {canEdit && (
                            <div style={{ 
                                textAlign: 'center', 
                                paddingTop: '30px',
                                borderTop: '2px solid #e0e0e0',
                                marginTop: '20px'
                            }}>
                                <Button
                                    primary
                                    size="large"
                                    type="submit"
                                    loading={saving}
                                    disabled={saving}
                                    icon
                                    labelPosition='right'
                                    style={{
                                        minWidth: '200px',
                                        padding: '12px 30px',
                                        fontSize: '1.1em'
                                    }}
                                >
                                    <Icon name='save' />
                                    Save Lifecycle Data
                                </Button>
                            </div>
                        )}
                    </Form>
                </Segment>
            </div>
        </LoadingWrapper>
    );
};
